import { faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { AiProcessingItem, AiProcessingStats } from '@/api/ai/types';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslator } from '@/hooks/useTranslator';
import { AI_PROCESSING_TRIGGER_ID } from '@/lib/constants/aiProcessing';
import { cn } from '@/lib/utils/cn';
import { ActiveJobsPopover } from './ActiveJobsPopover';
import { AiProcessingRing } from './AiProcessingRing';

interface AiProcessingStatProps {
  onOpen: () => void;
  stats: AiProcessingStats | null;
  inProgressItems: AiProcessingItem[];
  readyItems: AiProcessingItem[];
}

/**
 * The navbar AI-processing button: a sparkles icon + label, a progress ring
 * while jobs run, and an amber "N to review" badge for finished-but-unreviewed
 * jobs. Hovering while processing reveals `ActiveJobsPopover`; otherwise an
 * empty-state tooltip. Clicking always opens the full sheet.
 */
export function AiProcessingStat({
  onOpen,
  stats,
  inProgressItems,
  readyItems,
}: AiProcessingStatProps) {
  const { getTranslation } = useTranslator({ page: 'aiProcessing' });

  const percentage = stats?.percentage ?? 0;
  const isProcessing = (stats?.processing ?? 0) > 0;
  // "to review" badge: count only jobs that still have something to review
  // (readyItems = isItemReviewable), NOT `stats.completed`, which counts every
  // finished job — including ones the user already reviewed — and over-counts.
  const reviewCount = readyItems.length;

  const button = (
    <button
      type='button'
      id={AI_PROCESSING_TRIGGER_ID}
      onClick={onOpen}
      className={cn(
        'flex items-center gap-1.5 rounded-md px-1.5 h-7.5 transition-colors hover:bg-accent',
        isProcessing || reviewCount > 0
          ? 'text-foreground'
          : 'text-muted-foreground',
      )}
    >
      <FontAwesomeIcon icon={faSparkles} className='text-sm' />
      <span className='hidden lg:inline text-xs font-medium whitespace-nowrap'>
        {getTranslation('stat.title')}
      </span>
      {/* Ring shows overall progress of running jobs; it hides at 100% (no jobs
          processing), and the "to review" badge then carries the finished jobs.
          Both can show at once — one job running while another awaits review. */}
      {isProcessing && <AiProcessingRing percentage={percentage} />}
      {reviewCount > 0 && (
        <StatusBadge
          variant='warning'
          label={getTranslation('stat.toReview', { count: reviewCount })}
        />
      )}
    </button>
  );

  // While jobs run, hovering reveals the rich progress popover. Otherwise the
  // plain empty-state tooltip (the badge already surfaces anything to review).
  if (isProcessing) {
    return (
      <ActiveJobsPopover
        jobsTotal={stats?.total ?? 0}
        runningCount={stats?.processing ?? 0}
        readyCount={reviewCount}
        inProgressItems={inProgressItems}
        readyItems={readyItems}
        onOpenQueue={onOpen}
      >
        {button}
      </ActiveJobsPopover>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger render={button} />
      <TooltipContent side='bottom' className='max-w-56 text-pretty'>
        {getTranslation('stat.tooltipEmpty')}
      </TooltipContent>
    </Tooltip>
  );
}
